/* eslint-disable string-to-lingui/missing-lingui-transformation */
import {useMatches, useLocation, Navigate} from 'react-router-dom'

import config from 'config'
import {invariant} from '~/error/invariant'
import {reportError} from '~/error/report'
import type {Role} from '~/shell/user'
import {useHasAllowedRole, useUser} from '~/shell/user'

import type {AppRouteHandle, CompanyStatus} from './types'
import {useHasIncompleteSignup} from './use-has-incomplete-signup'

function useHasCompanyRequiredStatus(minCompanyStatus?: CompanyStatus) {
    const user = useUser()

    if (!minCompanyStatus) {
        return true
    }

    if (minCompanyStatus === 'sdd') {
        return !!user?.company?.status.sdd
    }

    if (minCompanyStatus === 'fdd') {
        return !!user?.company?.status.fdd
    }

    return false
}

type RouteMatch = ReturnType<typeof useMatches>[number]

type AppRouteMatch = RouteMatch & {handle: Required<AppRouteHandle>}

const hasMatchHandleKey =
    <Key extends keyof AppRouteHandle>(key: Key) =>
    (match: RouteMatch): match is AppRouteMatch => {
        return (match.handle as AppRouteHandle)?.[key] !== undefined
    }

// check that none of the route allowedRoles are less restrictive than the parent routes' allowedRoles
const checkAllowedRolesNesting = (matches: RouteMatch[]) => {
    const matchesWithParam = matches.filter(hasMatchHandleKey('allowedRoles'))
    const allowedRoles = matchesWithParam.map((match) => match.handle.allowedRoles)

    allowedRoles.forEach((allowed, index) => {
        const previousRoles = allowedRoles.slice(0, index)
        const forbiddenRoles = allowed.filter((role) =>
            previousRoles.some((previous) => !previous.includes(role)),
        )
        const pathname = matchesWithParam[index].pathname
        invariant(
            Number(forbiddenRoles.length) === 0,
            `Nested route "${pathname}" wants to give access to roles forbidden in parent routes: ${forbiddenRoles.join(
                ',',
            )}.`,
        )
    })
}

export const getLastHandleParam = <Key extends keyof AppRouteHandle>(
    matches: RouteMatch[],
    key: Key,
) => {
    const matchesWithParam = matches.filter(hasMatchHandleKey(key))
    if (key === 'allowedRoles') {
        checkAllowedRolesNesting(matches)
    }
    return matchesWithParam[matchesWithParam.length - 1]?.handle?.[key]
}

export const useLastHandleParam = <Key extends keyof AppRouteHandle>(key: Key) => {
    const matches = useMatches()
    return getLastHandleParam(matches, key)
}

/**
 * Component wrapping all behind-the-login-screen routes, only to be used ONCE in core/routes.
 * Takes care of:
 * 1. redirecting:
 * - anonymous users to the login screen (setting a redirect back to the requested page)
 * - previously logged in user with the Otp session to login with passcode
 * - users who have not completed the onboarding (e.g. no ToS acceptance)
 * 2. disallowing access based on user role or company status. If the conditions are not met, the user will be shown Access Denied screen.
 */
export const AuthRouteProvider = ({children}: {children?: React.ReactNode}) => {
    const auth = useLastHandleParam('auth')

    if (!auth) {
        return <>{children}</>
    }
    return <AuthRoute>{children}</AuthRoute>
}

export const AuthRoute = ({children}: {children: React.ReactNode}) => {
    const location = useLocation()
    const user = useUser()

    const allowedRoles = useLastHandleParam('allowedRoles')
    const minCompanyStatus = useLastHandleParam('minCompanyStatus')

    const hasIncompleteSignup = useHasIncompleteSignup()

    const hasAllowedRole = useHasAllowedRole(allowedRoles)
    // TODO: change this check to `isCompanyFDDd`.
    const hasCompanyRequiredStatus = useHasCompanyRequiredStatus(minCompanyStatus)
    // if in an active registration flow
    if (hasIncompleteSignup) {
        return <Navigate to="/register" />
    }

    if (!user) {
        const searchParams = new URLSearchParams(location.search)

        // "userAccountEmail" is a standard key from send-notification space rock
        // https://github.com/pleo-io/space-rocks/blob/main/src/rocks/send-notification/index.ts#L197
        const email = searchParams.get('userAccountEmail') || null
        const redirect = location.pathname + location.search

        return <Navigate to="/login" state={{email, redirect}} />
    }

    // Make sure every user has accepted terms, except for admins still creating the company
    if (!user?.status?.acceptedTerms && user?.companyId) {
        return <Navigate to="/register/terms" />
    }

    if (!hasAllowedRole || !hasCompanyRequiredStatus) {
        return <Navigate to="/" />
    }

    return <>{children}</>
}

let reportedError = false

// Extension of invariant helper, throws error in dev but only reports error in prod
// TODO: remove once we're confident the new auth checks are reliable in prod
const invariantSafe = (condition: boolean, message: string) => {
    const isDevOrFeature = ['dev', 'feature', 'test'].includes(config.environment)
    if (isDevOrFeature) {
        invariant(condition, message)
    } else if (!condition && !reportedError) {
        reportedError = true
        reportError(message)
    }
}

const allowedRolesEmptyError = (pathname: string) =>
    `Not allowed to use this hook under route "${pathname}": "allowedRoles" param for this route is empty or undefined.`

const forbiddenRolesError = (pathname: string, allowedRoles: string, forbiddenRoles: string) =>
    `Not allowed to use this hook under route "${pathname}": the hook is only allowed to be used in routes with allowed roles: ${allowedRoles}; but route allows roles: ${forbiddenRoles}.`

const missingRequiredRolesError = (pathname: string, allowedRoles: string) =>
    `Not allowed to use this hook under route "${pathname}": the hook is allowed to be used in routes with one of allowed roles: ${allowedRoles}.`

export const checkAllowedRolesFromRoute = (
    pathname: string,
    allowedRoles: Role[],
    allowedRolesFromRoute: Role[] = [],
) => {
    // Check if allowedRolesFromRoute array is empty
    invariantSafe(Number(allowedRolesFromRoute.length) > 0, allowedRolesEmptyError(pathname))

    // Check that route doesn't allow access to users with roles that are forbidden in the context of this hook
    const forbiddenRoles = allowedRolesFromRoute.filter(
        (allowedRole) => !allowedRoles.includes(allowedRole),
    )
    invariantSafe(
        Number(forbiddenRoles.length) === 0,
        forbiddenRolesError(pathname, allowedRoles.join(','), forbiddenRoles.join(',')),
    )

    // Check that route allows access to users with any role that is required in the context of this hook
    const hasRequiredRole = allowedRoles.some((role) => allowedRolesFromRoute.includes(role))
    invariantSafe(hasRequiredRole, missingRequiredRolesError(pathname, allowedRoles.join(',')))
}

/**
 * Throws error if this hook is used inside a route which
 * - doesn't allow any of the provided roles, or
 * - allows roles which are not in the provided roles
 * @param allowedRoles
 */
export function useAllowedRoles(allowedRoles: Role[]) {
    const {pathname} = useLocation()
    const allowedRolesFromRoute = useLastHandleParam('allowedRoles')

    const auth = useLastHandleParam('auth')
    invariantSafe(auth, `Not allowed to use this hook under public (non-auth) route ${pathname}.`)

    checkAllowedRolesFromRoute(pathname, allowedRoles, allowedRolesFromRoute)
}
